import { Link } from "react-router-dom";
import { Home as HomeIcon, Phone, Mail, MapPin, Clock } from "lucide-react";
import { Seo } from "../components/Seo";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { StickyCall } from "../components/StickyCall";
import { LeadForm } from "../components/LeadForm";
import { Reveal } from "../components/Reveal";
import { PHONE, PHONE_HREF, PHONE2, PHONE2_HREF, EMAIL, ADDRESS, MAP_EMBED, EXTERNAL } from "../lib/site";

const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

const InfoRow = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-4">
    <span className="shrink-0 w-11 h-11 rounded-xl bg-[#d90429]/10 flex items-center justify-center">
      <Icon className="w-5 h-5 text-[#d90429]" />
    </span>
    <div>
      <p className="text-xs uppercase tracking-wider font-bold text-slate-500">{label}</p>
      <div className="mt-1 text-[#0b192c] font-medium">{children}</div>
    </div>
  </div>
);

export default function ContactPage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Urgent Passport & Visa",
    telephone: ["+1-202-457-4665", "+1-866-334-8472"],
    email: EMAIL,
    address: {
      "@type": "PostalAddress",
      streetAddress: "1629 K St NW #300",
      addressLocality: "Washington",
      addressRegion: "DC",
      postalCode: "20006",
      addressCountry: "US",
    },
    url: "https://upvservices.com/contact",
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact Us | Urgent Passport & Visa — Washington DC"
        description="Contact UPV Services in downtown Washington DC. Call 202-457-4665 or 866-334-8472, email info@upvservices.com, or visit 1629 K St NW #300. Get a fast passport & visa quote."
        path="/contact"
        jsonLd={[jsonLd]}
      />
      <Header />
      <main id="main-content" tabIndex={-1}>
        <section className="relative bg-[#0b192c] text-white overflow-hidden" data-testid="contact-hero">
          <div className="noise-overlay" />
          <div className="relative max-w-7xl mx-auto px-6 md:px-8 pt-16 pb-16 md:pt-20 md:pb-20">
            <nav className="flex items-center gap-2 text-sm text-white/60 mb-8" aria-label="Breadcrumb">
              <Link to="/" onClick={scrollTop} className="hover:text-white inline-flex items-center gap-1"><HomeIcon className="w-3.5 h-3.5" /> Home</Link>
              <span>/</span>
              <span className="text-white/90">Contact</span>
            </nav>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#ef233c]">Get in touch</span>
            <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-[58px] tracking-tight max-w-3xl">
              Talk to a passport & visa specialist.
            </h1>
            <p className="mt-6 text-lg text-white/75 max-w-2xl leading-relaxed">
              Conveniently located in downtown Washington DC where the embassies and federal offices are. Call, email, or send us your details and we'll respond quickly.
            </p>
          </div>
        </section>

        <section className="bg-white py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-8 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="space-y-8">
                  <InfoRow icon={MapPin} label="Office Address">
                    1629 K Street NW<br />Suite 300<br />Washington, DC 20006
                  </InfoRow>
                  <InfoRow icon={Phone} label="Phone">
                    <a href={PHONE_HREF} className="block hover:text-[#d90429] transition-colors" data-testid="contact-phone-1">{PHONE}</a>
                    <a href={PHONE2_HREF} className="block hover:text-[#d90429] transition-colors" data-testid="contact-phone-2">{PHONE2}</a>
                  </InfoRow>
                  <InfoRow icon={Mail} label="Email">
                    <a href={`mailto:${EMAIL}`} className="hover:text-[#d90429] transition-colors" data-testid="contact-email">{EMAIL}</a>
                  </InfoRow>
                  <InfoRow icon={Clock} label="Hours">
                    Monday – Friday · 9:00 AM – 5:00 PM
                  </InfoRow>
                </div>

                <div className="mt-8 rounded-2xl overflow-hidden border border-black/5 shadow-[0_8px_30px_rgba(11,25,44,0.08)]" data-testid="contact-map">
                  <iframe
                    title="UPV Services office location map"
                    src={MAP_EMBED}
                    width="100%"
                    height="320"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
                <a href={EXTERNAL.google} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#d90429] hover:underline" data-testid="contact-directions">
                  <MapPin className="w-4 h-4" /> Get directions on Google Maps
                </a>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <div className="lg:sticky lg:top-24">
                <LeadForm sourcePage="contact" />
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <StickyCall />
    </div>
  );
}
